/**
 * Progress Diagnostics - Feature-flagged debug logging for quiz/progress issues
 *
 * Activate: Add ?debugProgress=1 to URL (works in prod too)
 *
 * This file adds NO business logic changes - only diagnostic output.
 * All functions are no-ops when flag is off.
 */

/** Pure helper so unit tests can assert the gate without loading the module flag. */
export function isDebugProgressEnabled(search?: string): boolean {
  const raw =
    search ??
    (typeof window !== 'undefined' ? window.location.search : '');
  try {
    return new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw).get('debugProgress') === '1';
  } catch {
    return false;
  }
}

/** Off by default in production; enable with ?debugProgress=1 */
export const DEBUG_PROGRESS = isDebugProgressEnabled();

// Log once on load if enabled
if (DEBUG_PROGRESS) {
  console.log('%c[PROGRESS-DIAG] 🔍 Diagnostics ENABLED', 'color: #f59e0b; font-weight: bold; font-size: 14px');
  console.log('[PROGRESS-DIAG] URL:', window.location.href);
  console.log('[PROGRESS-DIAG] Timestamp:', new Date().toISOString());
}

/**
 * Mask sensitive tokens for logging (show first 6 + last 4 chars)
 */
export function maskToken(token?: string | null): string {
  if (!token) return '(none)';
  if (token.length <= 12) return token.slice(0, 4) + '...';
  return token.slice(0, 6) + '...' + token.slice(-4);
}

/**
 * Create a summary of quiz progress state
 */
export function summarizeProgress(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== 'object') return { empty: true };
  const d = data as Record<string, unknown>;
  return {
    score: d.score ?? '?',
    totalPoints: d.totalPoints ?? '?',
    answersCount: Array.isArray(d.answers) ? d.answers.length : 0,
    currentQuestionIndex: d.currentQuestionIndex ?? '?',
    isCompleted: d.isCompleted ?? false,
    showQuizResults: d.showQuizResults ?? false
  };
}

/**
 * Log identity snapshot - shows current player/session state
 */
export function diagSnapshot(label: string, extra?: Record<string, unknown>) {
  if (!DEBUG_PROGRESS) return;
  
  console.log(`%c[PROGRESS-DIAG] 📸 ${label}`, 'color: #3b82f6; font-weight: bold', {
    timestamp: new Date().toISOString(),
    route: window.location.pathname + window.location.search,
    ...extra
  });
}

/**
 * Log RPC request with params (masks tokens)
 */
export function diagRpcRequest(rpcName: string, params: Record<string, unknown>) {
  if (!DEBUG_PROGRESS) return;
  
  // Mask any session tokens in params
  const maskedParams = { ...params };
  if (maskedParams.p_session_token) {
    maskedParams.p_session_token = maskToken(maskedParams.p_session_token as string);
  }
  
  console.log(`%c[PROGRESS-DIAG] 📤 ${rpcName} REQUEST`, 'color: #10b981', maskedParams);
}

/**
 * Log RPC response with summary
 */
export function diagRpcResponse(rpcName: string, result: unknown, error?: unknown) {
  if (!DEBUG_PROGRESS) return;
  
  if (error) {
    console.log(`%c[PROGRESS-DIAG] ❌ ${rpcName} ERROR`, 'color: #ef4444; font-weight: bold', { error });
  } else {
    console.log(`%c[PROGRESS-DIAG] 📥 ${rpcName} RESPONSE`, 'color: #10b981', {
      hasData: !!result,
      summary: summarizeProgress(result)
    });
  }
}

/**
 * Log store state changes
 */
export function diagStoreWrite(
  storeName: string, 
  reason: string, 
  before: unknown, 
  after: unknown
) {
  if (!DEBUG_PROGRESS) return;
  
  console.log(`%c[PROGRESS-DIAG] 📝 ${storeName} WRITE`, 'color: #8b5cf6; font-weight: bold', {
    reason,
    before: summarizeProgress(before),
    after: summarizeProgress(after)
  });
}

/**
 * Log cache hits/misses for RPC calls
 */
export function diagCacheEvent(
  type: 'HIT' | 'MISS' | 'EXPIRED',
  rpcName: string,
  cacheKey: string,
  extra?: { ageMs?: number; ttlMs?: number }
) {
  if (!DEBUG_PROGRESS) return;
  
  const emoji = type === 'HIT' ? '💾' : type === 'MISS' ? '🔄' : '⏰';
  const color = type === 'HIT' ? '#22c55e' : type === 'MISS' ? '#f59e0b' : '#ef4444';
  
  console.log(`%c[PROGRESS-DIAG] ${emoji} CACHE ${type}: ${rpcName}`, `color: ${color}`, {
    cacheKey,
    ...extra
  });
}

/**
 * Log identity source - where player ID comes from
 */
export function diagIdentitySource(
  playerId: string | null | undefined,
  source: 'playerSession' | 'currentPlayer' | 'user' | 'anon',
  sessionToken?: string | null
) {
  if (!DEBUG_PROGRESS) return;
  
  const isAnon = source === 'anon' || !playerId;
  console.log(
    `%c[PROGRESS-DIAG] 🔑 IDENTITY: ${source}`,
    `color: ${isAnon ? '#ef4444' : '#22c55e'}; font-weight: bold`,
    {
      playerId: playerId || '(none)',
      playerIdLength: playerId?.length || 0,
      source,
      sessionToken: maskToken(sessionToken),
      isAnon
    }
  );
}

/**
 * Log skip events (when loading is skipped due to guards)
 */
export function diagSkip(label: string, reason: string, extra?: Record<string, unknown>) {
  if (!DEBUG_PROGRESS) return;
  
  console.log(`%c[PROGRESS-DIAG] ⏭️ SKIPPED: ${label}`, 'color: #f59e0b; font-weight: bold', {
    reason,
    ...extra
  });
}

/**
 * Final summary table for diagnosis
 */
export function diagSummary(results: {
  identityStable: boolean;
  fetchOccurred: boolean;
  fetchReturned: 'data' | 'empty' | 'error' | 'skipped';
  storeUpdated: boolean;
  overwriteOccurred: boolean;
  likelyCause: 'A' | 'B' | 'C' | 'D' | 'E' | 'unknown';
}) {
  if (!DEBUG_PROGRESS) return;
  
  console.log('%c[PROGRESS-DIAG] 📊 DIAGNOSIS SUMMARY', 'color: #ec4899; font-weight: bold; font-size: 16px');
  console.table({
    'Identity stable?': results.identityStable ? '✅ Yes' : '❌ No',
    'Fetch occurred?': results.fetchOccurred ? '✅ Yes' : '❌ No',
    'Fetch result': results.fetchReturned,
    'Store updated?': results.storeUpdated ? '✅ Yes' : '❌ No',
    'Overwrite occurred?': results.overwriteOccurred ? '❌ Yes (bad)' : '✅ No',
    'Likely cause': results.likelyCause
  });
  
  const causeDescriptions: Record<string, string> = {
    'A': 'loadProgress not called or skipped by guard',
    'B': 'RPC returned empty (wrong params/module/period)',
    'C': 'RPC returned data but store not updated (guard bug)',
    'D': 'Store updated but later reset by another effect',
    'E': 'Cache key uses "anon" due to identity hydration order',
    'unknown': 'Unable to determine - check full logs'
  };
  
  console.log(`%c[PROGRESS-DIAG] Cause ${results.likelyCause}: ${causeDescriptions[results.likelyCause]}`, 
    'color: #ec4899; font-weight: bold');
}
